import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Heart } from 'lucide-react';
import { motion } from 'framer-motion';

export default function AboutPage() {
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="container-praise py-8 px-4 sm:px-6">
        {/* Header */}
        <div className="relative mb-12">
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </motion.button>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mt-8"
          >
            <img 
              src="https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/mgqlogo2.png"
              alt="MyGoodQuotes Logo"
              className="h-16 sm:h-20 w-auto"
            />
          </motion.div>
        </div>

        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-3xl mx-auto"
        >
          {/* Letter Content */}
          <motion.div 
            variants={itemVariants}
            className="bg-white rounded-xl shadow-praise p-6 sm:p-12 space-y-6"
          >
            <div className="prose prose-lg max-w-none">
              <p className="text-lg sm:text-xl text-gray-600 mb-8">Hi there!</p>

              <p className="mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                It all started from my love of hearing all the funny and sometimes crazy things my kids would say. I wanted to remember them, read them later and share them with my family and friends. With a new journal in hand, my quote writing adventure began. I enjoyed reading these quotes to family and friends and seeing their reaction. Eventually, it turned into a way to stay in touch with the kids' grandparents. They got a lot of joy hearing about the next funny thing from the kids.
              </p>

              <p className="mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                Years later, I still love hearing what my kids(now adults) have to say and that has even expanded to other family members and friends. I learend that we all have something to say. It may come in quick funny lines from our kids as toddlers, teenagers and adults or words of wisdom from our grandparents and elders.
              </p>

              <p className="mb-6 text-base sm:text-lg text-gray-700 leading-relaxed">
                After briefly losing my quotes journal, I needed a simple way to capture those memories, share and most importantly, not lose them again. After some testing, tweaking and talking to people about saving family memories, I created MyGoodQuotes to share with the world. It's a simple way to recall all those moments in time from anyone, anytime, all in one place.
              </p>

              <p className="mb-8 text-lg sm:text-xl font-medium text-gray-900">Happy Quoting!</p>

              <div className="flex items-center gap-3">
                <a 
                  href="mailto:lacy@mygoodquotes.com"
                  className="inline-flex items-center gap-2 text-brand-600 hover:text-brand-700 transition-colors text-base sm:text-lg"
                >
                  <Mail className="h-5 w-5" />
                  lacy@mygoodquotes.com
                </a>
              </div>

              <div className="mt-6">
                <img
                  src="https://llmdocs.s3.us-east-1.amazonaws.com/mygoodquotes/blogimages/lmsign2.png"
                  alt="Lacy McDowell Signature"
                  className="h-12 sm:h-16 w-auto mb-2"
                />
                <p className="text-lg sm:text-xl font-display text-gray-900 mb-1">Lacy McDowell</p>
                <p className="text-base sm:text-lg text-gray-600">MyGoodQuotes Creator</p>
              </div>
              
              <div className="mt-12 pt-8 border-t border-gray-100 text-center">
                <div className="inline-flex items-center gap-2 text-brand-600">
                  <Heart className="h-5 w-5" />
                  <span className="text-sm sm:text-base">Made with love for families everywhere</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Call to Action */}
          <motion.div 
            variants={itemVariants}
            className="mt-8 text-center px-4"
          >
            <button
              onClick={() => navigate('/early-access')}
              className="btn-primary text-base sm:text-lg px-6 sm:px-8 py-3 w-full sm:w-auto"
            >
              Join the Early Access Program
            </button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}